import {
    Box,
    Flex,
    HStack,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TbSearch } from 'react-icons/tb';
import { Icon as YaxleIcon, IconButton as YaxleIconButton, Tooltip } from '../../ui';
import { QuotingTab } from '../../hooks/useGetOrderDetail';
import { getTableSettingsSx, TableSettings } from '../../hooks/useTableSettings';
import TableSettingsButton from '../TableSettingsButton';
import QuotingTabs from './QuotingTabs';

interface QuotingFullScreenProps {
    tabs: Array<QuotingTab>;
    orderId: string;
    vin: string;
    refetch: () => void | Promise<unknown>;
    onClose: () => void;
    tableSettings: TableSettings;
    onUpdateTableSettings: (newSettings: Partial<TableSettings>) => void;
}

const QuotingFullScreen = ({
    tabs,
    orderId,
    vin,
    refetch,
    onClose,
    tableSettings,
    onUpdateTableSettings,
}: QuotingFullScreenProps) => {
    const [searchQuery, setSearchQuery] = useState('');
    const tableSettingsSx = useMemo(() => getTableSettingsSx(tableSettings), [tableSettings]);

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    return (
        <Box
            className="partbay-fullscreen-backdrop"
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.500"
            zIndex={1000}
            display="flex"
            alignItems="stretch"
            justifyContent="center"
            pt={3}
            pb={3}
            px={3}
            transition="right 220ms ease"
            onClick={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <Box
                bg="white"
                borderRadius="xl"
                display="flex"
                flexDirection="column"
                overflow="hidden"
                w="100%"
                boxShadow="none"
                onClick={(event) => event.stopPropagation()}
            >
                <Flex
                    px={6}
                    py={3}
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    align="center"
                    justify="space-between"
                    flexShrink={0}
                    bg="white"
                    borderTopRadius="xl"
                >
                    <HStack spacing={3} flex={1} maxW="820px">
                        <InputGroup size="sm" flex={1}>
                            <InputLeftElement pointerEvents="none">
                                <Icon as={TbSearch as React.ElementType} color="gray.400" boxSize={4} />
                            </InputLeftElement>
                            <Input
                                placeholder="Search parts, part numbers..."
                                value={searchQuery}
                                onChange={(event) => setSearchQuery(event.target.value)}
                                borderRadius="md"
                                borderColor="gray.300"
                                _hover={{ borderColor: 'gray.400' }}
                                _focus={{ borderColor: '#0071E3', boxShadow: '0 0 0 1px #0071E3' }}
                                fontSize="sm"
                            />
                        </InputGroup>
                    </HStack>

                    <HStack spacing={2}>
                        <TableSettingsButton
                            settings={tableSettings}
                            onUpdateSettings={onUpdateTableSettings}
                        />
                        <Tooltip label="Exit full screen" placement="top">
                            <YaxleIconButton
                                aria-label="Exit full screen"
                                icon={<YaxleIcon name="collapse" size="sm" />}
                                size="toolbar"
                                variant="ghost"
                                colorScheme="gray"
                                color="fg.muted"
                                onClick={onClose}
                            />
                        </Tooltip>
                    </HStack>
                </Flex>

                <Box
                    flex={1}
                    overflowY="auto"
                    overflowX="auto"
                    sx={{
                        ...tableSettingsSx,
                        '&::-webkit-scrollbar': { width: '8px', height: '8px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { background: '#cbd5e0', borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb:hover': { background: '#a0aec0' },
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#cbd5e0 transparent',
                    }}
                >
                    <QuotingTabs
                        tabs={tabs}
                        orderId={orderId}
                        vin={vin}
                        refetch={refetch}
                        stackedOnly
                        searchQuery={searchQuery}
                        onSearchQueryChange={setSearchQuery}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default QuotingFullScreen;
